import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Icon, palette } from '@m12s/component-library';
import { useTranslation } from 'react-i18next';
import { check, falTimes } from 'lib/icons';
import { usePinVerification } from 'lib/hooks/usePinVerification';
import {
  ModalContainer,
  DisplayWrapper,
  Title,
  PinIndicatorsWrapper,
  PinDot,
  ErrorText,
  KeyboardWrapper,
  KeyboardRow,
  NumberButton,
  ActionButton,
  BackspaceButton,
  ActionButtonsWrapper,
  SubmitButton,
  SecondaryButton,
  SuccessContainer,
  SuccessCircle,
  SuccessText,
  LoadingSpinner,
  IconWrapper,
} from './styled';

const modalStyles = {
  width: '375px',
  maxWidth: '90vw',
  padding: 0,
  borderRadius: '16px',
};

const headerStyles = {
  display: 'none',
};

const detailsStyles = {
  padding: 0,
};

const PinVerificationModal = ({ isOpen, operatorId, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    verifyPin,
    isLoading,
    error: verificationError,
  } = usePinVerification({ operatorId });

  const modalRef = useRef(null);
  const successTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Handle keyboard input
  const handleKeyDown = useCallback(
    (event) => {
      // Only process events when modal is open and interactive
      if (!isOpen || isLoading || showSuccess) {
        return;
      }

      const { key } = event;

      // Handle number keys (0-9)
      if (key >= '0' && key <= '9') {
        event.preventDefault();
        if (pin.length < 6) {
          setPin((prevPin) => {
            return prevPin + key;
          });
          setError('');
        }
      }
      // Handle Backspace key - remove last digit
      else if (key === 'Backspace') {
        event.preventDefault();
        setPin((prevPin) => {
          return prevPin.slice(0, -1);
        });
        setError('');
      }
    },
    [isOpen, isLoading, showSuccess, pin.length]
  );

  // Reset PIN and error when component mounts or reopens
  useEffect(() => {
    if (isOpen) {
      setPin('');
      setError('');
      setShowSuccess(false);
    }
  }, [isOpen]);

  // Focus management - set focus to modal when it opens
  useEffect(() => {
    if (isOpen && !isLoading && !showSuccess && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen, isLoading, showSuccess]);

  // Keyboard event listener - attach to document for reliable capture
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [isOpen, handleKeyDown]);

  const handleNumberInput = (number) => {
    if (pin.length < 6) {
      setPin((prevPin) => {
        return prevPin + number;
      });
      setError('');
    }
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleBackspace = () => {
    setPin((prevPin) => {
      return prevPin.slice(0, -1);
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (pin.length === 0) {
      setError(t('Required'));
      return;
    }

    try {
      const result = await verifyPin(pin);

      if (result.valid) {
        setShowSuccess(true);
        successTimeoutRef.current = setTimeout(() => {
          return onSuccess();
        }, 1500);
      } else {
        const errorMessage = result?.error || t('Invalid PIN');
        setError(
          result.delay
            ? t('Account locked. Try again in {{seconds}} seconds.', {
                seconds: result.delay / 1000,
              })
            : errorMessage
        );
      }
    } catch (err) {
      setError(verificationError || t('Network error. Please try again.'));
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  // Generate pin indicators
  const renderPinIndicators = () => {
    const dots = [];
    for (let i = 0; i < pin.length; i += 1) {
      dots.push(<PinDot key={i} />);
    }
    return <PinIndicatorsWrapper>{dots}</PinIndicatorsWrapper>;
  };

  // Render number button
  const renderButton = (number) => {
    return (
      <NumberButton
        onClick={() => {
          return handleNumberInput(number);
        }}
        disabled={isLoading || showSuccess}
        type="button"
      >
        {number}
      </NumberButton>
    );
  };

  // Success content
  const renderSuccess = () => {
    return (
      <SuccessContainer>
        <SuccessCircle>
          <Icon icon={check} size="2rem" color={palette.White} />
        </SuccessCircle>
        <SuccessText>{t('Verification Successful')}</SuccessText>
      </SuccessContainer>
    );
  };

  // Main PIN entry content
  const renderPinEntry = () => {
    return (
      <ModalContainer ref={modalRef} tabIndex={-1}>
        <IconWrapper onClick={handleCancel}>
          <Icon icon={falTimes} size="1.5rem" color={palette.Grey500} />
        </IconWrapper>

        <DisplayWrapper>
          <Title>{t('Enter PIN to verify identity')}</Title>
          {renderPinIndicators()}
          <ErrorText $visible={!!error}>{error || 'Error'}</ErrorText>
        </DisplayWrapper>

        <KeyboardWrapper>
          <KeyboardRow>
            {renderButton('1')}
            {renderButton('2')}
            {renderButton('3')}
          </KeyboardRow>
          <KeyboardRow>
            {renderButton('4')}
            {renderButton('5')}
            {renderButton('6')}
          </KeyboardRow>
          <KeyboardRow>
            {renderButton('7')}
            {renderButton('8')}
            {renderButton('9')}
          </KeyboardRow>
          <KeyboardRow>
            <ActionButton
              onClick={handleClear}
              disabled={isLoading || showSuccess}
              type="button"
            >
              {t('Clear')}
            </ActionButton>
            {renderButton('0')}
            <BackspaceButton
              onClick={handleBackspace}
              disabled={isLoading || showSuccess}
              type="button"
            >
              ←
            </BackspaceButton>
          </KeyboardRow>
        </KeyboardWrapper>

        <ActionButtonsWrapper>
          <SubmitButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading || showSuccess}
          >
            {isLoading ? <LoadingSpinner /> : t('Submit')}
          </SubmitButton>

          <SecondaryButton
            variant="tertiary"
            onClick={handleCancel}
            disabled={isLoading || showSuccess}
          >
            {t('Cancel')}
          </SecondaryButton>
        </ActionButtonsWrapper>
      </ModalContainer>
    );
  };

  // Determine which content to render
  const getModalDetails = () => {
    if (showSuccess) {
      return renderSuccess();
    }
    return renderPinEntry();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      modalStyles={modalStyles}
      headerStyles={headerStyles}
      detailsStyles={detailsStyles}
      details={getModalDetails()}
    />
  );
};

PinVerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  operatorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PinVerificationModal;
